import { useCallback, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";

import { useGetCompanyName, useGetCompanyToken } from "../../../api/company";
import { usePostUserDetails, usePostUserLogin } from "../../../api/userDetails";
import { notificationSetupService } from "../../../config/firebase";
import { errorView } from "../../../helpers/ErrorHandler";
import { useAuthContext } from "../../../hooks/useAuthContext";
import { AuthTokenResponse, LoginResponse, UserProfile } from "../../../types/auth.types";
import { consumeReturnPath, removeTokens } from "../../../utils/cacheStorage";
import { AUTH_ROUTES } from "../constants/auth.constants";
import {
  persistCompanySession,
  persistLoginTokens,
  resolveUserCompany
} from "../helpers/authSession.helpers";
import { buildPasswordResetState } from "../helpers/passwordResetRoute";
import { LoginFormValues } from "../types/authForms.types";

/**
 * The login session bootstrap: login -> profile -> resolve the user's company
 * -> switch tenant -> persist tokens -> dispatch into `AuthContext` -> go to
 * "/". Extracted from the legacy `Login.tsx` (duplicated again in
 * `CognitoLogin.tsx`) so the new UI has a single, typed place for it. The
 * legacy screens are left as they are — see the redesign plan.
 */
export const useLoginSession = () => {
  const navigate = useNavigate();
  const { dispatch } = useAuthContext();

  const { mutate: loginUser, isLoading: isLoggingIn } = usePostUserLogin();
  const {
    data: userProfileData,
    mutate: fetchUserProfile,
    isLoading: isLoadingProfile
  } = usePostUserDetails();
  const { mutate: fetchCompanyToken, isLoading: isFetchingCompanyToken } = useGetCompanyToken();

  const userProfile = userProfileData;

  const {
    data: companies,
    status: companyFetchStatus,
    fetchStatus: companyFetchState
  } = useGetCompanyName(userProfile?.tenantId);

  // Guards the tenant-resolution effect so it runs once per fetched profile,
  // not on every unrelated re-render (the legacy screen's effect has no guard
  // and keeps re-triggering `handleCompanyInfoLoad`). Keyed by profile object
  // identity rather than tenant id: every login attempt fetches a fresh profile,
  // so a retry after a failed attempt (no company access, tenant switch error)
  // for the same tenant is resolved again instead of silently skipped.
  const resolvedForProfile = useRef<UserProfile | undefined>(undefined);

  const navigateToPasswordReset = useCallback(
    (email: string) => {
      navigate(AUTH_ROUTES.resetPassword, {
        state: buildPasswordResetState(true, email)
      });
    },
    [navigate]
  );

  const completeLogin = useCallback(
    (companyName: string, companyId: string, profile: UserProfile) => {
      notificationSetupService.setup(profile, () => {});
      dispatch({ type: "SET_COMPANY", payload: companyName });
      dispatch({ type: "SET_COMPANY_ID", payload: companyId });
      dispatch({ type: "LOGIN", payload: profile });
      navigate(consumeReturnPath() ?? "/");
    },
    [dispatch, navigate]
  );

  useEffect(() => {
    if (companyFetchStatus !== "success" || !companies || !userProfile?.tenantId) return;
    if (resolvedForProfile.current === userProfile) return;
    resolvedForProfile.current = userProfile;

    const resolved = resolveUserCompany(companies, userProfile);
    if (!resolved.status) {
      errorView("User has no company access.");
      removeTokens();
      return;
    }

    fetchCompanyToken(resolved.id, {
      onSuccess: (tokenData: AuthTokenResponse) => {
        persistCompanySession(tokenData, resolved.tenantId, resolved.tenantId);
        completeLogin(resolved.name, resolved.id, userProfile);
      },
      onError: () => errorView("Unable to open your company. Please try again.")
    });
  }, [companyFetchStatus, companies, userProfile, fetchCompanyToken, completeLogin]);

  const handleLoginSuccess = useCallback(
    (response: LoginResponse, email: string) => {
      removeTokens();
      persistLoginTokens(response);
      if (response.isPasswordResetRequired) {
        navigateToPasswordReset(email);
      } else {
        fetchUserProfile("");
      }
    },
    [fetchUserProfile, navigateToPasswordReset]
  );

  const submitLogin = useCallback(
    (values: LoginFormValues) => {
      loginUser(values, {
        onSuccess: response => handleLoginSuccess(response, values.email)
      });
    },
    [loginUser, handleLoginSuccess]
  );

  const isPending =
    isLoggingIn ||
    isLoadingProfile ||
    isFetchingCompanyToken ||
    (companyFetchStatus === "loading" && companyFetchState === "fetching");

  return { submitLogin, isPending };
};
